// src/features/affiliate/ProfileDialog.tsx
// One dialog, two jobs: apply to become an affiliate (CreateAffiliate) and
// edit an existing profile including payout preferences (UpdateAffiliate).

import { useEffect, useRef, useState } from 'react';
import { observer } from '@react';
import { useAffiliate, useI18n } from '@react/hooks';
import { Alert } from '@react/components';
import { FormDialog, TitleInput, AutoGrowTextarea, FormRow } from '@/components/form';
import { Select } from '@/components/Select';
import { ToggleSwitch } from '@/components/ToggleSwitch';
import { useScope } from '@/hooks/useScope';
import type { Affiliate, BillingPeriod, Currency, PaymentMethod } from '@core/types/affiliate';
import { CurrencyValues } from '@core/types/affiliate';
import { errMessage, makeEntity } from './format';

const PAYMENT_METHODS: PaymentMethod[] = [
  'PAYMENT_METHOD_BANK_TRANSFER',
  'PAYMENT_METHOD_WIRE_TRANSFER',
  'PAYMENT_METHOD_ACH',
  'PAYMENT_METHOD_SEPA',
  'PAYMENT_METHOD_DIGITAL_WALLET',
  'PAYMENT_METHOD_CRYPTO',
  'PAYMENT_METHOD_INVOICE',
  'PAYMENT_METHOD_BALANCE',
];

const BILLING_PERIODS: BillingPeriod[] = [
  'BILLING_PERIOD_WEEKLY',
  'BILLING_PERIOD_BIWEEKLY',
  'BILLING_PERIOD_MONTHLY',
  'BILLING_PERIOD_QUARTERLY',
];

interface ProfileDialogProps {
  open: boolean;
  onClose: () => void;
  /** null = apply as a new affiliate. Provided = edit an existing profile. */
  affiliate: Affiliate | null;
  onSaved: (affiliate: Affiliate) => void;
}

export const ProfileDialog = observer(({ open, onClose, affiliate, onSaved }: ProfileDialogProps) => {
  const { t } = useI18n();
  const scope = useScope();
  const store = useAffiliate();
  const isEdit = !!affiliate;
  const descRef = useRef<HTMLTextAreaElement>(null);

  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [website, setWebsite] = useState('');
  const [telegram, setTelegram] = useState('');
  const [skype, setSkype] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [currency, setCurrency] = useState<Currency>('CURRENCY_USD');
  const [preferredMethod, setPreferredMethod] = useState<PaymentMethod>('PAYMENT_METHOD_BANK_TRANSFER');
  const [minimumPayout, setMinimumPayout] = useState('');
  const [payoutSchedule, setPayoutSchedule] = useState<BillingPeriod>('BILLING_PERIOD_MONTHLY');
  const [autoPayout, setAutoPayout] = useState(false);
  const [moreFields, setMoreFields] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setDisplayName(affiliate?.display_name ?? '');
    setDescription(affiliate?.description ?? '');
    setEmail(affiliate?.email ?? '');
    setPhone(affiliate?.phone ?? '');
    setWebsite(affiliate?.website ?? '');
    setTelegram(affiliate?.telegram ?? '');
    setSkype(affiliate?.skype ?? '');
    setReferralCode('');
    const prefs = affiliate?.payout_preferences;
    setCurrency(prefs?.currency ?? 'CURRENCY_USD');
    setPreferredMethod(prefs?.preferred_method ?? 'PAYMENT_METHOD_BANK_TRANSFER');
    setMinimumPayout(prefs?.minimum_payout ? String(prefs.minimum_payout.amount / Math.pow(10, prefs.minimum_payout.decimal_places || 2)) : '');
    setPayoutSchedule(prefs?.payout_schedule ?? 'BILLING_PERIOD_MONTHLY');
    setAutoPayout(prefs?.auto_payout ?? false);
    setMoreFields(false);
  }, [open, affiliate]);

  const dirty = !!(displayName.trim() || description.trim() || email.trim());

  const save = async () => {
    if (!displayName.trim() || !email.trim()) return;
    setIsSaving(true);
    setError(null);
    try {
      if (isEdit && affiliate?.entity) {
        const minorUnits = minimumPayout ? Math.round(Number(minimumPayout) * 100) : 0;
        const saved = await store.UpdateAffiliate(affiliate.entity.id, {
          entity: affiliate.entity,
          update_mask: ['display_name', 'description', 'email', 'phone', 'website', 'telegram', 'skype', 'payout_preferences'],
          display_name: displayName.trim(),
          description,
          email: email.trim(),
          phone,
          website,
          telegram,
          skype,
          payout_preferences: {
            currency,
            preferred_method: preferredMethod,
            minimum_payout: { amount: minorUnits, currency, decimal_places: 2 },
            payout_schedule: payoutSchedule,
            auto_payout: autoPayout,
          },
          metadata: affiliate.metadata ?? {},
        });
        onSaved(saved);
      } else {
        const saved = await store.CreateAffiliate({
          entity: makeEntity('', 'affiliate', scope),
          display_name: displayName.trim(),
          description,
          email: email.trim(),
          phone,
          website,
          telegram,
          skype,
          referral_code: referralCode || undefined,
          metadata: {},
        });
        onSaved(saved);
      }
      onClose();
    } catch (e) {
      setError(errMessage(e, t(isEdit ? 'affiliate.profile.saveError' : 'affiliate.profile.applyError')));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      onSubmit={save}
      submitLabel={isEdit ? t('common.saveChanges') : t('affiliate.profile.apply')}
      canSubmit={!!displayName.trim() && !!email.trim()}
      isSubmitting={isSaving}
      dirty={dirty}
      aria-label={isEdit ? t('affiliate.profile.editTitle') : t('affiliate.profile.applyTitle')}
      footerLeft={
        <button
          type="button"
          onClick={() => setMoreFields((v) => !v)}
          className="text-xs text-muted-foreground hover:text-foreground"
        >
          {moreFields ? t('formKit.fewerFields') : t('formKit.moreFields')}
        </button>
      }
    >
      <div className="px-3.5 pb-1 pt-2">
        {error && (
          <div className="pb-2">
            <Alert variant="error">{error}</Alert>
          </div>
        )}
        {!isEdit && <p className="pb-2 text-sm text-muted-foreground">{t('affiliate.profile.applyHint')}</p>}
        <TitleInput
          value={displayName}
          onChange={setDisplayName}
          placeholder={`${t('affiliate.profile.displayName')}…`}
          initialFocus
          onEnter={() => descRef.current?.focus()}
          aria-label={t('affiliate.profile.displayName')}
        />
        <AutoGrowTextarea ref={descRef} value={description} onChange={setDescription} placeholder={`${t('common.description')}…`} minRows={1} />
      </div>

      <div className="space-y-1 px-3.5 pb-2">
        <FormRow label={t('affiliate.profile.email')} labelWidth="sm">
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} aria-label={t('affiliate.profile.email')} className="w-full rounded-md border border-input bg-background px-2.5 py-1.5 text-sm text-foreground outline-none focus:ring-2 focus:ring-ring" />
        </FormRow>
        <FormRow label={t('affiliate.profile.phone')} labelWidth="sm">
          <input value={phone} onChange={(e) => setPhone(e.target.value)} aria-label={t('affiliate.profile.phone')} className="w-full rounded-md border border-input bg-background px-2.5 py-1.5 text-sm text-foreground outline-none focus:ring-2 focus:ring-ring" />
        </FormRow>
      </div>

      {moreFields && (
        <div className="space-y-1 border-t border-border px-3.5 py-2">
          <div className="grid grid-cols-1 gap-x-3 sm:grid-cols-3">
            <FormRow label={t('affiliate.profile.website')} labelWidth="sm">
              <input value={website} onChange={(e) => setWebsite(e.target.value)} className="w-full rounded-md border border-input bg-background px-2.5 py-1.5 text-sm text-foreground outline-none focus:ring-2 focus:ring-ring" />
            </FormRow>
            <FormRow label={t('affiliate.profile.telegram')} labelWidth="sm">
              <input value={telegram} onChange={(e) => setTelegram(e.target.value)} className="w-full rounded-md border border-input bg-background px-2.5 py-1.5 text-sm text-foreground outline-none focus:ring-2 focus:ring-ring" />
            </FormRow>
            <FormRow label={t('affiliate.profile.skype')} labelWidth="sm">
              <input value={skype} onChange={(e) => setSkype(e.target.value)} className="w-full rounded-md border border-input bg-background px-2.5 py-1.5 text-sm text-foreground outline-none focus:ring-2 focus:ring-ring" />
            </FormRow>
          </div>
          {!isEdit && (
            <FormRow label={t('affiliate.profile.referralCode')} labelWidth="sm" hint={t('affiliate.profile.referralCodeHint')}>
              <input value={referralCode} onChange={(e) => setReferralCode(e.target.value)} className="w-full max-w-xs rounded-md border border-input bg-background px-2.5 py-1.5 font-mono text-sm text-foreground outline-none focus:ring-2 focus:ring-ring" />
            </FormRow>
          )}

          {isEdit && (
            <div className="space-y-1 border-t border-border pt-2">
              <h4 className="pb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">{t('affiliate.profile.payoutPreferences')}</h4>
              <FormRow label={t('affiliate.field.currency')} labelWidth="sm">
                <Select value={currency} onChange={(e) => setCurrency(e.target.value as Currency)} options={CurrencyValues.filter((v) => v !== 'CURRENCY_UNSPECIFIED').map((v) => ({ value: v, label: v.replace('CURRENCY_', '') }))} aria-label={t('affiliate.field.currency')} fullWidth={false} className="w-32" />
              </FormRow>
              <FormRow label={t('affiliate.profile.preferredMethod')} labelWidth="sm">
                <Select value={preferredMethod} onChange={(e) => setPreferredMethod(e.target.value as PaymentMethod)} options={PAYMENT_METHODS.map((v) => ({ value: v, label: t(`affiliate.paymentMethod.${v.replace('PAYMENT_METHOD_', '').toLowerCase()}`) }))} aria-label={t('affiliate.profile.preferredMethod')} fullWidth={false} className="w-56" />
              </FormRow>
              <FormRow label={t('affiliate.profile.minimumPayout')} labelWidth="sm">
                <input
                  type="text"
                  inputMode="decimal"
                  value={minimumPayout}
                  onChange={(e) => setMinimumPayout(e.target.value.replace(/[^0-9.]/g, ''))}
                  placeholder="0.00"
                  aria-label={t('affiliate.profile.minimumPayout')}
                  className="w-28 rounded-md border border-input bg-background px-2.5 py-1.5 text-right text-sm tabular-nums text-foreground outline-none focus:ring-2 focus:ring-ring"
                />
              </FormRow>
              <FormRow label={t('affiliate.profile.payoutSchedule')} labelWidth="sm">
                <Select value={payoutSchedule} onChange={(e) => setPayoutSchedule(e.target.value as BillingPeriod)} options={BILLING_PERIODS.map((v) => ({ value: v, label: t(`affiliate.billingPeriod.${v.replace('BILLING_PERIOD_', '').toLowerCase()}`) }))} aria-label={t('affiliate.profile.payoutSchedule')} fullWidth={false} className="w-40" />
              </FormRow>
              <FormRow label={t('affiliate.profile.autoPayout')} labelWidth="sm">
                <ToggleSwitch label="" checked={autoPayout} onChange={setAutoPayout} />
              </FormRow>
            </div>
          )}
        </div>
      )}
    </FormDialog>
  );
});
